import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Webhook, Plus, ShieldCheck, ShieldAlert, Ban, CheckCircle2, RefreshCw, Copy, Check,
  ArrowLeft, Loader2, AlertTriangle, KeyRound, Link2, Trash2, Eye, EyeOff, Lock, Upload,
} from "lucide-react";
import { AdminLayout } from "./AdminLayout";
import { api, apiErr } from "../../lib/api";
import { Loader, Empty, GoldButton, GhostButton } from "../../components/UI";
import { toast } from "sonner";

const STATUS_STYLE = {
  ACTIVE: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  SUSPENDED: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};
const LIFECYCLE_STYLE = {
  DRAFT: "bg-slate-500/15 text-slate-300 border-slate-500/40",
  READY: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  ACTIVE: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  SUSPENDED: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};
const PRODUCT_OPTIONS = ["SPORTSBOOK", "ARENABOOK", "CHALLENGE", "STRIKE5"];
const CONNECT_MODES = ["CODE", "REDIRECT", "BOTH"];
const TRUST_STYLE = {
  VERIFIED: "bg-[#E5B842]/15 text-[#E5B842] border-[#E5B842]/40",
  UNVERIFIED: "bg-slate-500/15 text-slate-300 border-slate-500/40",
};
const EV_STATUS_STYLE = {
  ACCEPTED: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  HELD: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  REJECTED: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

function Pill({ value, map, testid }) {
  return (
    <span data-testid={testid} className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border ${map[value] || "bg-slate-700/30 text-slate-300 border-slate-600"}`}>
      {value}
    </span>
  );
}

function CopyField({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); } catch { /* ignore */ }
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="mt-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <code data-testid={testid} className="flex-1 min-w-0 truncate bg-[#0a0a0a] border border-[rgba(212,175,55,0.25)] rounded px-3 py-2 text-xs text-[#E5B842] font-mono">{value}</code>
        <button onClick={copy} data-testid={`copy-${testid}`} className="shrink-0 inline-flex items-center gap-1 px-2.5 py-2 rounded bg-[#161616] border border-[rgba(212,175,55,0.25)] text-slate-300 hover:text-[#E5B842] text-xs" style={{ transition: "color 0.15s ease" }}>
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
}

function PushToProductionModal({ partnerId, onClose, onDone }) {
  const [email, setEmail] = useState("anarud76@gmail.com");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!email || !password) { toast.error("Email και password απαιτούνται"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(`/admin/partners/${partnerId}/push-activation-to-production`,
        { production_url: "https://wosbf.com", admin_email: email, admin_password: password });
      setResult(data);
      if (data.ok) {
        toast.success("Εγκαταστάθηκε στο production");
        onDone && onDone(data);
      } else {
        toast.error(`Production: ${data.production_http} — δες λεπτομέρειες παρακάτω`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setResult({ ok: false, stage: "preview", production_body: detail || { error: "PREVIEW_ERROR", message: apiErr(e, "Push failed") } });
      toast.error(apiErr(e, "Push failed"));
    } finally { setBusy(false); setPassword(""); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="push-to-production-modal" onClick={onClose}>
      <div className="w-full max-w-xl rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.30)] p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-lg font-black uppercase tracking-tight text-slate-50 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-[#E5B842]" /> Push to Production
        </h3>
        <p className="text-[11px] text-slate-500 mt-1">Server-to-server εγκατάσταση. Το password σου χρησιμοποιείται μόνο σε αυτή τη σύνδεση, ΔΕΝ γράφεται πουθενά, ΔΕΝ αποθηκεύεται.</p>

        {!result && (
          <>
            <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-mono mt-4 mb-1.5">Production admin email</label>
            <input data-testid="push-email-input" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-[#E5B842]/60 font-mono" />
            <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-mono mt-3 mb-1.5">Production admin password</label>
            <input data-testid="push-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Password του admin σου στο wosbf.com"
              className="w-full bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E5B842]/60 font-mono" />
            <div className="flex justify-end gap-2 mt-5">
              <GhostButton onClick={onClose} data-testid="push-cancel">Άκυρο</GhostButton>
              <GoldButton onClick={submit} disabled={busy} data-testid="push-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                Εγκατάσταση τώρα
              </GoldButton>
            </div>
          </>
        )}

        {result && result.ok && (
          <div className="mt-4 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/40" data-testid="push-success">
            <div className="flex items-center gap-2 text-emerald-300 font-black">
              <CheckCircle2 className="w-5 h-5" /> Έγινε!
            </div>
            <p className="text-sm text-emerald-200 mt-2">Το partner Assos365 εγκαταστάθηκε με επιτυχία στο production ({result.production_body?.created ? "νέα εγγραφή" : "ανανέωση παραγωγικού slot"}).</p>
            <pre className="mt-3 text-[10px] text-emerald-200/70 bg-black/40 rounded p-2 overflow-auto max-h-40">{JSON.stringify({ok:result.ok, env: result.production_body?.env, created: result.production_body?.created, modified: result.production_body?.modified, hmac_source: result.production_body?.hmac_source, partner_id: result.production_body?.partner?.partner_id}, null, 2)}</pre>
            <div className="flex justify-end mt-3">
              <GhostButton onClick={onClose} data-testid="push-close">Κλείσιμο</GhostButton>
            </div>
          </div>
        )}

        {result && !result.ok && (
          <div className="mt-4 p-4 rounded-lg bg-red-500/10 border border-red-500/40" data-testid="push-error">
            <div className="flex items-center gap-2 text-red-300 font-black">
              <AlertTriangle className="w-5 h-5" /> Αποτυχία ({result.stage})
            </div>
            <p className="text-sm text-red-200 mt-2">
              Production HTTP: <b>{result.production_http ?? "—"}</b>
              {result.production_http === 405 && <> — <i>Η production δεν έχει ακόμα το endpoint. Χρειάζεται ένα Republish.</i></>}
              {result.production_http === 401 && result.stage === "login" && <> — <i>Λάθος email ή password στο wosbf.com.</i></>}
            </p>
            <pre className="mt-3 text-[10px] text-red-200/80 bg-black/40 rounded p-2 overflow-auto max-h-40">{JSON.stringify(result.production_body ?? {}, null, 2)}</pre>
            <div className="flex justify-end gap-2 mt-3">
              <GhostButton onClick={() => setResult(null)} data-testid="push-retry">Δοκίμασε ξανά</GhostButton>
              <GhostButton onClick={onClose} data-testid="push-close">Κλείσιμο</GhostButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CopyActivationBundleButton({ partnerId }) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/partners/${partnerId}/activation-bundle`);
      const json = JSON.stringify(data.bundle, null, 2);
      await navigator.clipboard.writeText(json);
      setCopied(true);
      toast.success("Activation bundle copied — paste into production admin");
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      toast.error(apiErr(e, "Could not copy activation bundle"));
    } finally { setBusy(false); }
  };
  return (
    <GhostButton onClick={copy} disabled={busy} data-testid="copy-activation-bundle-btn">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />)}
      {copied ? "Copied bundle" : "Copy Activation Bundle"}
    </GhostButton>
  );
}

function ProductionCredentialsPanel({ partnerId, partnerSlug }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState({});
  const [copiedKey, setCopiedKey] = useState(null);
  const [copiedAll, setCopiedAll] = useState(false);
  const [pushOpen, setPushOpen] = useState(false);

  const reveal = async () => {
    if (!window.confirm("Reveal production credentials? This action is written to the admin audit log.")) return;
    setBusy(true);
    try {
      const { data: d } = await api.get(`/admin/partners/${partnerId}/production-credentials`);
      setData(d);
    } catch (e) {
      toast.error(apiErr(e, "Reveal failed"));
    } finally {
      setBusy(false);
    }
  };

  const copyLine = async (line, key) => {
    try { await navigator.clipboard.writeText(line); setCopiedKey(key); setTimeout(() => setCopiedKey(null), 1500); }
    catch { toast.error("Could not copy"); }
  };
  const copyAll = async () => {
    try { await navigator.clipboard.writeText((data.env_lines || []).join("\n")); setCopiedAll(true); setTimeout(() => setCopiedAll(false), 1500); }
    catch { toast.error("Could not copy"); }
  };
  const toggle = (key) => setShown((s) => ({ ...s, [key]: !s[key] }));
  const mask = (v) => "•".repeat(Math.min(24, Math.max(8, (v || "").length)));

  if (partnerSlug !== "assos365") return null;

  return (
    <div className="mt-6 rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.30)] p-5" data-testid="production-credentials-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-[#E5B842]" />
            <h3 className="font-heading text-lg font-black uppercase tracking-tight text-slate-50">Production Credentials</h3>
          </div>
          <p className="text-xs text-slate-500 mt-1">Secure one-time reveal for pasting into the Assos365 project <b>Manage → Secrets</b>. Audit-logged.</p>
        </div>
        {!data && (
          <div className="flex flex-wrap gap-2">
            <GoldButton onClick={() => setPushOpen(true)} data-testid="push-to-production-btn">
              <ShieldCheck className="w-4 h-4" /> ΑΠΕΥΘΕΙΑΣ ΕΓΚΑΤΑΣΤΑΣΗ ΣΤΟ PRODUCTION
            </GoldButton>
            <CopyActivationBundleButton partnerId={partnerId} />
            <GhostButton onClick={reveal} disabled={busy} data-testid="reveal-prod-credentials-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
              Reveal (fallback)
            </GhostButton>
          </div>
        )}
        {data && (
          <div className="flex flex-wrap gap-2">
            <GoldButton onClick={() => setPushOpen(true)} data-testid="push-to-production-btn">
              <ShieldCheck className="w-4 h-4" /> ΑΠΕΥΘΕΙΑΣ ΕΓΚΑΤΑΣΤΑΣΗ ΣΤΟ PRODUCTION
            </GoldButton>
            <CopyActivationBundleButton partnerId={partnerId} />
          </div>
        )}
      </div>
      {pushOpen && <PushToProductionModal partnerId={partnerId} onClose={() => setPushOpen(false)} />}

      {data && (
        <div className="mt-4 space-y-3" data-testid="production-credentials-panel">
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/40" data-testid="prod-creds-warning">
            <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-200">
              <b>Επικόλλησε μόνο στο Assos365 project → Manage → Secrets. Ποτέ στο chat.</b>
            </p>
          </div>

          <div className="flex items-center justify-end">
            <GoldButton onClick={copyAll} data-testid="prod-creds-copy-all">
              {copiedAll ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copiedAll ? "Copied all" : "Copy all"}
            </GoldButton>
          </div>

          {(data.env_lines || []).map((line) => {
            const [k, ...rest] = line.split("=");
            const v = rest.join("=");
            const isShown = !!shown[k];
            const displayLine = `${k}=${isShown ? v : mask(v)}`;
            return (
              <div key={k} className="rounded-lg bg-[#0a0a0a] border border-[rgba(212,175,55,0.20)] p-3" data-testid={`prod-env-row-${k}`}>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1.5">{k}</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 min-w-0 truncate bg-[#000] border border-[rgba(212,175,55,0.18)] rounded px-3 py-2 text-xs text-[#E5B842] font-mono" data-testid={`prod-env-value-${k}`}>
                    {displayLine}
                  </code>
                  <button onClick={() => toggle(k)} data-testid={`prod-env-toggle-${k}`}
                    className="shrink-0 inline-flex items-center gap-1 px-2.5 py-2 rounded bg-[#161616] border border-[rgba(212,175,55,0.25)] text-slate-300 hover:text-[#E5B842] text-xs"
                    style={{ transition: "color 0.15s ease" }} title={isShown ? "Hide" : "Reveal"} aria-label={isShown ? "Hide value" : "Reveal value"}>
                    {isShown ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                  <button onClick={() => copyLine(line, k)} data-testid={`prod-env-copy-${k}`}
                    className="shrink-0 inline-flex items-center gap-1 px-2.5 py-2 rounded bg-[#161616] border border-[rgba(212,175,55,0.25)] text-slate-300 hover:text-[#E5B842] text-xs"
                    style={{ transition: "color 0.15s ease" }} aria-label={`Copy ${k}`}>
                    {copiedKey === k ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ImportBundleModal({ onClose, onImported }) {
  const [bundleText, setBundleText] = useState("");
  const [hmacSecret, setHmacSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [showHmac, setShowHmac] = useState(false);

  const submit = async () => {
    let bundle;
    try { bundle = JSON.parse(bundleText); }
    catch { toast.error("Bundle JSON is not valid"); return; }
    if (!hmacSecret || hmacSecret.length < 32) {
      toast.error("HMAC secret must be at least 32 characters"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/partners/import-activation-bundle",
        { bundle, hmac_secret: hmacSecret, env: "production" });
      toast.success(data.created ? "Partner created in production" : "Production slot updated");
      onImported(data);
    } catch (e) {
      toast.error(apiErr(e, "Import failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="import-bundle-modal" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.30)] p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-lg font-black uppercase tracking-tight text-slate-50 flex items-center gap-2">
          <Upload className="w-5 h-5 text-[#E5B842]" /> Import Partner Activation Bundle
        </h3>
        <p className="text-[11px] text-slate-500 mt-1">Idempotently installs or updates a partner's <b>production</b> env slot + credentials.production hashes. Audit-logged. Never logs the HMAC value.</p>

        <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
          <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-200">
            The bundle contains NO plaintext secrets. The HMAC secret below is used in-session only (transmitted over HTTPS, stored in Mongo for signature verification, never logged).
          </p>
        </div>

        <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-mono mt-4 mb-1.5">Activation bundle JSON</label>
        <textarea data-testid="import-bundle-json-input" value={bundleText} onChange={(e) => setBundleText(e.target.value)}
          placeholder='{"version":1,"partner":{"slug":"assos365", ...}}'
          rows={10}
          className="w-full bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-3 py-2 text-xs text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E5B842]/60 font-mono" />

        <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-mono mt-4 mb-1.5">HMAC secret (hex, ≥32 chars)</label>
        <div className="flex items-center gap-2">
          <input data-testid="import-bundle-hmac-input" type={showHmac ? "text" : "password"} value={hmacSecret} onChange={(e) => setHmacSecret(e.target.value)}
            placeholder="Paste the plaintext production HMAC secret"
            className="flex-1 bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E5B842]/60 font-mono" />
          <button onClick={() => setShowHmac((s) => !s)} data-testid="import-bundle-hmac-toggle"
            className="shrink-0 inline-flex items-center gap-1 px-2.5 py-2 rounded bg-[#161616] border border-[rgba(212,175,55,0.25)] text-slate-300 hover:text-[#E5B842] text-xs"
            style={{ transition: "color 0.15s ease" }} aria-label={showHmac ? "Hide HMAC" : "Reveal HMAC"}>
            {showHmac ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
        </div>
        <p className="text-[10px] text-slate-600 mt-1">If the bundle includes an integrity verifier, the backend will reject a mismatched HMAC.</p>

        <div className="flex justify-end gap-2 mt-5">
          <GhostButton onClick={onClose} data-testid="import-bundle-cancel">Cancel</GhostButton>
          <GoldButton onClick={submit} disabled={busy} data-testid="import-bundle-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Install into production
          </GoldButton>
        </div>
      </div>
    </div>
  );
}

function KeyRevealModal({ data, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="key-reveal-modal" onClick={onClose}>
      <div className="w-full max-w-lg rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.3)] p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-lg font-black uppercase tracking-tight text-slate-50 flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-[#E5B842]" /> Partner credentials
        </h3>
        <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
          <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-200">Shown <b>only once</b>. Copy and store them securely now — they cannot be retrieved again. {data.warning}</p>
        </div>
        <CopyField label="API Key" value={data.api_key} testid="apikey" />
        <CopyField label="HMAC Secret (used to sign requests)" value={data.hmac_secret} testid="secret" />
        <div className="flex justify-end mt-5">
          <GoldButton onClick={onClose} data-testid="key-reveal-done"><Check className="w-4 h-4" /> I've stored them</GoldButton>
        </div>
      </div>
    </div>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [trust, setTrust] = useState("UNVERIFIED");
  const [rate, setRate] = useState(120);
  const [types, setTypes] = useState("DUEL_RESULT, COUPON_SETTLED");
  const [connectMode, setConnectMode] = useState("BOTH");
  const [products, setProducts] = useState(["SPORTSBOOK"]);
  const [authorizationUrl, setAuthorizationUrl] = useState("");
  const [embedOrigins, setEmbedOrigins] = useState("");
  const [healthUrl, setHealthUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const toggleProduct = (p) => setProducts((cur) => cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]);
  const submit = async () => {
    if (name.trim().length < 2) { toast.error("Name too short"); return; }
    setBusy(true);
    try {
      const allowed = types.split(",").map((t) => t.trim()).filter(Boolean);
      const origins = embedOrigins.split(/[\s,]+/).map((o) => o.trim()).filter(Boolean);
      const { data } = await api.post("/admin/partners", {
        name: name.trim(), trust, rate_limit_per_min: Number(rate) || 120,
        allowed_event_types: allowed,
        connect_mode: connectMode,
        allowed_products: products,
        authorization_url: authorizationUrl.trim() || null,
        embed_origins: origins,
        health_url: healthUrl.trim() || null,
        lifecycle_status: "DRAFT",
      });
      toast.success("Partner created (DRAFT) — run Test Connection next");
      onCreated(data);
    } catch (e) { toast.error(apiErr(e, "Failed to create partner")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="partner-create-modal" onClick={onClose}>
      <div className="w-full max-w-xl rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.25)] p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-lg font-black uppercase tracking-tight text-slate-50 flex items-center gap-2">
          <Plus className="w-5 h-5 text-[#E5B842]" /> New Partner · Wizard
        </h3>
        <p className="text-[11px] text-slate-500 mt-1">Starts in DRAFT. Run Test Connection and promote to ACTIVE to open ingestion.</p>

        <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-mono mt-4 mb-1.5">Partner name</label>
        <input data-testid="partner-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Acme Arena"
          className="w-full bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E5B842]/60" />

        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-1.5">Trust</label>
            <select data-testid="partner-trust-select" value={trust} onChange={(e) => setTrust(e.target.value)}
              className="w-full bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-[#E5B842]/60">
              <option value="UNVERIFIED">UNVERIFIED</option>
              <option value="VERIFIED">VERIFIED</option>
            </select>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-1.5">Connect mode</label>
            <select data-testid="partner-mode-select" value={connectMode} onChange={(e) => setConnectMode(e.target.value)}
              className="w-full bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-[#E5B842]/60">
              {CONNECT_MODES.map((m) => (<option key={m} value={m}>{m}</option>))}
            </select>
          </div>
        </div>

        <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-mono mt-3 mb-1.5">Product allowlist</label>
        <div className="flex flex-wrap gap-2" data-testid="partner-products-group">
          {PRODUCT_OPTIONS.map((p) => (
            <button key={p} type="button" onClick={() => toggleProduct(p)} data-testid={`partner-product-${p}`}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-heading font-bold uppercase tracking-wide border ${products.includes(p) ? "bg-[#E5B842] text-black border-[#E5B842]" : "bg-[#161616] border-[rgba(212,175,55,0.22)] text-slate-300 hover:text-[#E5B842]"}`}>
              {p}
            </button>
          ))}
        </div>

        <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-mono mt-3 mb-1.5">Authorization URL (REDIRECT / BOTH)</label>
        <input data-testid="partner-auth-url-input" value={authorizationUrl} onChange={(e) => setAuthorizationUrl(e.target.value)} placeholder="https://partner.example.com/connect/wsbf"
          className="w-full bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E5B842]/60" />

        <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-mono mt-3 mb-1.5">Embed origins (https, comma/space separated)</label>
        <textarea data-testid="partner-embed-origins-input" value={embedOrigins} onChange={(e) => setEmbedOrigins(e.target.value)} placeholder="https://partner.example.com https://www.partner.example.com" rows={2}
          className="w-full bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E5B842]/60 font-mono" />
        <p className="text-[10px] text-slate-600 mt-0.5">Fail-closed: only these origins may frame the embed (CSP frame-ancestors).</p>

        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-1.5">Rate limit / min</label>
            <input data-testid="partner-rate-input" type="number" value={rate} onChange={(e) => setRate(e.target.value)}
              className="w-full bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-[#E5B842]/60" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-1.5">Partner health URL (optional)</label>
            <input data-testid="partner-health-url-input" value={healthUrl} onChange={(e) => setHealthUrl(e.target.value)} placeholder="https://partner.example.com/healthz"
              className="w-full bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-[#E5B842]/60" />
          </div>
        </div>

        <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-mono mt-3 mb-1.5">Allowed event types (comma-separated)</label>
        <input data-testid="partner-types-input" value={types} onChange={(e) => setTypes(e.target.value)}
          className="w-full bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-[#E5B842]/60" />

        <div className="flex justify-end gap-2 mt-5">
          <GhostButton onClick={onClose} data-testid="partner-create-cancel">Cancel</GhostButton>
          <GoldButton onClick={submit} disabled={busy} data-testid="partner-create-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create as DRAFT
          </GoldButton>
        </div>
      </div>
    </div>
  );
}

function TestConnectionPanel({ partner, onLifecycle }) {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/partners/${partner.partner_id}/test-connection`);
      setResult(data);
      toast[data.ok ? "success" : "error"](data.ok ? "All required checks passed" : "Checks failed — see details");
    } catch (e) { toast.error(apiErr(e, "Test connection failed")); }
    finally { setBusy(false); }
  };
  const promote = async (to) => {
    try {
      await api.post(`/admin/partners/${partner.partner_id}/lifecycle`, { lifecycle_status: to });
      toast.success(`Lifecycle: ${to}`);
      onLifecycle();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const lc = partner.lifecycle_status || (partner.status === "ACTIVE" ? "ACTIVE" : "SUSPENDED");
  return (
    <div className="space-y-4" data-testid="partner-test-conn-panel">
      <div className="rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.20)] p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-[#E5B842]">Universal Onboarding</div>
            <div className="text-slate-100 text-sm">Lifecycle · <Pill value={lc} map={LIFECYCLE_STYLE} testid={`partner-lc-${lc}`} /></div>
            <p className="text-[11px] text-slate-500 mt-1">DRAFT → READY (test passes) → ACTIVE (ingestion opens) → SUSPENDED.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <GhostButton onClick={run} disabled={busy} data-testid="partner-test-conn-btn"><ShieldCheck className="w-4 h-4" /> {busy ? "Testing…" : "Test Connection"}</GhostButton>
            {lc === "DRAFT" && <GoldButton onClick={() => promote("READY")} data-testid="partner-promote-ready"><CheckCircle2 className="w-4 h-4" /> Promote to READY</GoldButton>}
            {lc === "READY" && <GoldButton onClick={() => promote("ACTIVE")} data-testid="partner-promote-active"><CheckCircle2 className="w-4 h-4" /> Promote to ACTIVE</GoldButton>}
            {lc === "ACTIVE" && <GhostButton onClick={() => promote("SUSPENDED")} data-testid="partner-promote-suspended"><Ban className="w-4 h-4" /> Suspend</GhostButton>}
            {lc === "SUSPENDED" && <GoldButton onClick={() => promote("ACTIVE")} data-testid="partner-promote-reactivate"><CheckCircle2 className="w-4 h-4" /> Reactivate</GoldButton>}
          </div>
        </div>
      </div>
      {result && (
        <div className="rounded-xl bg-[#0a0a0a] border border-[rgba(212,175,55,0.18)] p-4" data-testid="partner-test-conn-result">
          <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-2">Checklist · ran at {new Date(result.ran_at).toLocaleTimeString()}</div>
          <ul className="divide-y divide-[rgba(212,175,55,0.08)]">
            {result.checks.map((c) => (
              <li key={c.id} data-testid={`check-${c.id}`} className="py-2 flex items-start gap-3 text-sm">
                {c.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 text-rose-400 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[12px] text-slate-200 flex items-center gap-2">
                    {c.id} {c.optional && <span className="text-[10px] uppercase tracking-wider text-slate-500 border border-slate-700 rounded px-1">optional</span>}
                  </div>
                  <div className="text-[11px] text-slate-500 break-words">{c.detail}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function PartnerList() {
  const [data, setData] = useState(null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [reveal, setReveal] = useState(null);
  const nav = useNavigate();

  const load = useCallback(async () => {
    setData(null);
    try { const { data } = await api.get("/admin/partners"); setData(data); }
    catch (e) { toast.error(apiErr(e, "Failed to load partners")); setData({ partners: [], total: 0 }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <AdminLayout>
      <div data-testid="admin-partners-page">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#E5B842] font-mono font-bold mb-2">Partner Network</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-slate-50 leading-none flex items-center gap-3">
              <Webhook className="w-8 h-8 text-[#E5B842] shrink-0" /> Partners
            </h1>
            <p className="text-slate-400 mt-2 text-sm sm:text-base">External sites that send game results into the ArenaBook network.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <GhostButton onClick={() => setImporting(true)} data-testid="partner-import-bundle-btn"><Upload className="w-4 h-4" /> Import Activation Bundle</GhostButton>
            <GoldButton onClick={() => setCreating(true)} data-testid="partner-new-btn"><Plus className="w-4 h-4" /> New Partner</GoldButton>
          </div>
        </div>

        {data === null ? (
          <Loader label="Loading partners…" />
        ) : data.partners.length === 0 ? (
          <Empty label="No partners yet" />
        ) : (
          <div className="rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.20)] divide-y divide-[rgba(212,175,55,0.10)]" data-testid="partners-list">
            {data.partners.map((p) => (
              <button key={p.partner_id} onClick={() => nav(`/admin/partners/${p.partner_id}`)} data-testid={`partner-row-${p.partner_id}`}
                className="w-full text-left p-4 flex flex-col lg:flex-row lg:items-center gap-3 hover:bg-[#151515]" style={{ transition: "background-color 0.15s ease" }}>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-heading text-base font-bold uppercase tracking-tight text-slate-50 break-words">{p.name}</span>
                    <Pill value={p.status} map={STATUS_STYLE} testid={`partner-status-${p.status}`} />
                    <Pill value={p.trust} map={TRUST_STYLE} testid={`partner-trust-${p.trust}`} />
                    {p.is_sandbox && <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300 border border-sky-500/30">sandbox</span>}
                  </div>
                  <div className="text-xs text-slate-500 font-mono mt-1 break-all">
                    slug: {p.slug} · {p.rate_limit_per_min}/min · last used: {p.last_used_at ? new Date(p.last_used_at).toLocaleString() : "never"}
                  </div>
                </div>
                <div className="flex gap-4 shrink-0 text-xs font-mono">
                  <span className="text-emerald-300">{p.event_counts?.ACCEPTED || 0} <span className="text-slate-600">acc</span></span>
                  <span className="text-amber-300">{p.event_counts?.HELD || 0} <span className="text-slate-600">held</span></span>
                  <span className="text-slate-400">{p.event_counts?.total || 0} <span className="text-slate-600">total</span></span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {creating && <CreateModal onClose={() => setCreating(false)} onCreated={(d) => { setCreating(false); setReveal(d); load(); }} />}
      {importing && <ImportBundleModal onClose={() => setImporting(false)} onImported={() => { setImporting(false); load(); }} />}
      {reveal && <KeyRevealModal data={reveal} onClose={() => setReveal(null)} />}
    </AdminLayout>
  );
}

const EV_FILTERS = ["ALL", "ACCEPTED", "HELD", "REJECTED"];

function PartnerDetail({ id }) {
  const [p, setP] = useState(null);
  const [events, setEvents] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [tab, setTab] = useState("events");
  const [linksData, setLinksData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState(null);
  const nav = useNavigate();

  const loadPartner = useCallback(async () => {
    try { const { data } = await api.get(`/admin/partners/${id}`); setP(data); }
    catch (e) { toast.error(apiErr(e, "Failed to load partner")); nav("/admin/partners"); }
  }, [id, nav]);

  const loadEvents = useCallback(async () => {
    setEvents(null);
    try { const { data } = await api.get(`/admin/partners/${id}/events`, { params: { status: filter, page: 1, page_size: 25 } }); setEvents(data); }
    catch { setEvents({ events: [], total: 0 }); }
  }, [id, filter]);

  const loadLinks = useCallback(async () => {
    setLinksData(null);
    try { const { data } = await api.get(`/admin/partners/${id}/links`, { params: { status: "ALL" } }); setLinksData(data); }
    catch { setLinksData({ links: [], total: 0 }); }
  }, [id]);

  useEffect(() => { loadPartner(); }, [loadPartner]);
  useEffect(() => { loadEvents(); }, [loadEvents]);
  useEffect(() => { if (tab === "links") loadLinks(); }, [tab, loadLinks]);

  const adminRevokeLink = async (link) => {
    if (!window.confirm(`Revoke link ${link.partner_user_id} ↔ ${link.wsbf_player_id}?`)) return;
    try { await api.post(`/admin/partners/${id}/links/${link.id}/revoke`); toast.success("Link revoked"); loadLinks(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const act = async (action) => {
    const verb = action === "suspend" ? "Suspend" : "Activate";
    if (!window.confirm(`${verb} ${p.name}?`)) return;
    setBusy(true);
    try { await api.post(`/admin/partners/${id}/${action}`); toast.success(`Partner ${action}d`); loadPartner(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const rotate = async () => {
    if (!window.confirm("Rotate credentials? The current API key and secret stop working immediately.")) return;
    setBusy(true);
    try { const { data } = await api.post(`/admin/partners/${id}/rotate-key`); setReveal(data); loadPartner(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!p) return <AdminLayout><Loader label="Loading partner…" /></AdminLayout>;

  return (
    <AdminLayout>
      <div data-testid="admin-partner-detail">
        <Link to="/admin/partners" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-slate-400 hover:text-[#E5B842] mb-5"><ArrowLeft className="w-3.5 h-3.5" /> All partners</Link>

        <div className="rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.20)] p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-heading text-3xl font-black uppercase tracking-tight text-slate-50 break-words">{p.name}</h1>
                <Pill value={p.status} map={STATUS_STYLE} testid={`partner-status-${p.status}`} />
                <Pill value={p.trust} map={TRUST_STYLE} testid={`partner-trust-${p.trust}`} />
                {p.is_sandbox && <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300 border border-sky-500/30">sandbox</span>}
              </div>
              <div className="text-xs text-slate-500 font-mono mt-2 break-all space-y-0.5">
                <div>slug: <span className="text-slate-300">{p.slug}</span> · key ••••{p.api_key_last4 || "----"}</div>
                <div>{p.rate_limit_per_min}/min · types: {(p.allowed_event_types || []).join(", ")}</div>
                <div>last used: {p.last_used_at ? new Date(p.last_used_at).toLocaleString() : "never"}{p.rotated_at ? ` · rotated: ${new Date(p.rotated_at).toLocaleString()}` : ""}</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              {p.status === "ACTIVE" ? (
                <GhostButton onClick={() => act("suspend")} disabled={busy} data-testid="partner-suspend-btn"><Ban className="w-4 h-4" /> Suspend</GhostButton>
              ) : (
                <GoldButton onClick={() => act("activate")} disabled={busy} data-testid="partner-activate-btn"><CheckCircle2 className="w-4 h-4" /> Activate</GoldButton>
              )}
              <GhostButton onClick={rotate} disabled={busy} data-testid="partner-rotate-btn"><RefreshCw className="w-4 h-4" /> Rotate key</GhostButton>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-5">
            {[["ACCEPTED", "emerald"], ["HELD", "amber"], ["total", "slate"]].map(([k]) => (
              <div key={k} className="rounded-lg bg-[#0a0a0a] border border-[rgba(212,175,55,0.14)] p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{k === "total" ? "Total" : k}</div>
                <div className="font-heading text-2xl font-black text-slate-50 tabular-nums mt-1">{p.event_counts?.[k] ?? 0}</div>
              </div>
            ))}
          </div>
        </div>

        {p.slug === "assos365" && <ProductionCredentialsPanel partnerId={id} partnerSlug={p.slug} />}

        <div className="mt-8">
          <div className="flex flex-wrap items-center gap-2 mb-5 border-b border-[rgba(212,175,55,0.14)]">
            {[["events", "Recent Events"], ["links", "Linked Users"], ["preview", "Ranking Preview"], ["onboarding", "Onboarding"]].map(([k, label]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`partner-tab-${k}`}
                className={`px-4 py-2.5 text-sm font-heading font-bold uppercase tracking-wide -mb-px border-b-2 ${tab === k ? "border-[#E5B842] text-[#E5B842]" : "border-transparent text-slate-400 hover:text-slate-200"}`}
                style={{ transition: "color 0.15s ease" }}>{label}</button>
            ))}
          </div>

          {tab === "events" && (
          <>
          <div className="flex flex-wrap items-center justify-end gap-3 mb-4">
            <div className="flex flex-wrap gap-2" data-testid="events-filters">
              {EV_FILTERS.map((f) => (
                <button key={f} onClick={() => setFilter(f)} data-testid={`events-filter-${f}`}
                  className={`px-3 py-1.5 rounded-lg text-[11px] font-heading font-bold uppercase tracking-wide ${filter === f ? "bg-[#E5B842] text-black" : "bg-[#161616] border border-[rgba(212,175,55,0.22)] text-slate-300 hover:text-[#E5B842]"}`}
                  style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}>{f}</button>
              ))}
            </div>
          </div>
          {events === null ? (
            <Loader label="Loading events…" />
          ) : events.events.length === 0 ? (
            <Empty label="No events for this filter" />
          ) : (
            <div className="overflow-x-auto rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.20)]" data-testid="partner-events-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-slate-500 font-mono border-b border-[rgba(212,175,55,0.14)]">
                    <th className="text-left px-4 py-2.5">Event ID</th>
                    <th className="text-left px-4 py-2.5">Type</th>
                    <th className="text-left px-4 py-2.5">Player Ref</th>
                    <th className="text-left px-4 py-2.5">Resolved</th>
                    <th className="text-left px-4 py-2.5">Status</th>
                    <th className="text-left px-4 py-2.5 whitespace-nowrap">Received</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[rgba(212,175,55,0.08)]">
                  {events.events.map((e) => (
                    <tr key={e.event_id} data-testid={`partner-event-${e.event_id}`} className="text-slate-300">
                      <td className="px-4 py-2.5 font-mono text-xs break-all">{e.event_id}</td>
                      <td className="px-4 py-2.5 font-mono text-xs">{e.event_type}</td>
                      <td className="px-4 py-2.5 font-mono text-xs break-all">{e.player_ref}</td>
                      <td className="px-4 py-2.5 font-mono text-xs break-all text-[#E5B842]">{e.resolved_player_ref || "—"}</td>
                      <td className="px-4 py-2.5"><Pill value={e.status} map={EV_STATUS_STYLE} /></td>
                      <td className="px-4 py-2.5 text-[11px] text-slate-500 font-mono whitespace-nowrap">{e.received_at ? new Date(e.received_at).toLocaleString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          </>
          )}

          {tab === "preview" && <RankingPreview partner={p} />}

          {tab === "onboarding" && <TestConnectionPanel partner={p} onLifecycle={loadPartner} />}

          {tab === "links" && (
            linksData === null ? (
              <Loader label="Loading links…" />
            ) : linksData.links.length === 0 ? (
              <Empty label="No linked users yet" />
            ) : (
              <div className="overflow-x-auto rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.20)]" data-testid="partner-links-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-slate-500 font-mono border-b border-[rgba(212,175,55,0.14)]">
                      <th className="text-left px-4 py-2.5">Partner User ID</th>
                      <th className="text-left px-4 py-2.5">WSTF Player</th>
                      <th className="text-left px-4 py-2.5">Linked</th>
                      <th className="text-left px-4 py-2.5">Status</th>
                      <th className="text-right px-4 py-2.5">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgba(212,175,55,0.08)]">
                    {linksData.links.map((l) => (
                      <tr key={l.id} data-testid={`partner-link-${l.id}`} className="text-slate-300">
                        <td className="px-4 py-2.5 font-mono text-xs break-all">{l.partner_user_id}</td>
                        <td className="px-4 py-2.5 font-mono text-xs break-all text-[#E5B842]">{l.wsbf_player_id}</td>
                        <td className="px-4 py-2.5 text-[11px] text-slate-500 font-mono whitespace-nowrap">{l.linked_at ? new Date(l.linked_at).toLocaleString() : "—"}</td>
                        <td className="px-4 py-2.5"><Pill value={l.status} map={{ ACTIVE: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", REVOKED: "bg-slate-500/15 text-slate-400 border-slate-500/40" }} /></td>
                        <td className="px-4 py-2.5 text-right">
                          {l.status === "ACTIVE" && (
                            <button onClick={() => adminRevokeLink(l)} data-testid={`partner-link-revoke-${l.id}`}
                              className="inline-flex items-center gap-1 text-xs text-rose-300 hover:text-rose-200"><Trash2 className="w-3.5 h-3.5" /> Revoke</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      </div>
      {reveal && <KeyRevealModal data={reveal} onClose={() => setReveal(null)} />}
    </AdminLayout>
  );
}

// --- Phase 3: Partner Ranking Preview + Embed Code -----------------------
const PREVIEW_PRODUCTS = [
  { key: "OVERALL", label: "Overall" },
  { key: "ARENABOOK", label: "ArenaBook" },
  { key: "CHALLENGE", label: "Challenge" },
  { key: "STRIKE5", label: "Strike 5" },
  { key: "SPORTSBOOK", label: "Sportsbook" },
];
const PREVIEW_SUBS = [
  { key: "ALL", label: "All" }, { key: "PREGAME", label: "Pregame" }, { key: "LIVE", label: "Live" },
];

function RankingPreview({ partner }) {
  const [product, setProduct] = useState("OVERALL");
  const [subtype, setSubtype] = useState("ALL");
  const [data, setData] = useState(null);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const base = typeof window !== "undefined" ? window.location.origin : "";
  const api_base = (process.env.REACT_APP_BACKEND_URL || base).replace(/\/$/, "");
  const dataUrl = `${api_base}/api/embed/rankings/${partner.slug}/data?product=${product}&page=1&limit=25${product === "SPORTSBOOK" ? `&subtype=${subtype}` : ""}`;
  const embedUrl = `${api_base}/api/embed/rankings/${partner.slug}`;
  const embedCode = `<iframe src="${embedUrl}" style="width:100%;min-height:620px;border:0;" title="WSTF Official Ranking — ${partner.name}" loading="lazy" referrerpolicy="no-referrer"></iframe>`;

  useEffect(() => {
    setData(null);
    fetch(dataUrl).then((r) => r.json()).then(setData).catch(() => setData({ error: true, items: [] }));
  }, [dataUrl]);

  const copyEmbed = async () => {
    try { await navigator.clipboard.writeText(embedCode); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Could not copy to clipboard"); }
  };

  const rows = data?.items || [];
  const sportsbookMode = product === "SPORTSBOOK";

  return (
    <div className="space-y-4" data-testid="partner-ranking-preview">
      <div className="rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.20)] p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-wider text-[#E5B842]">Partner Preview</div>
            <div className="text-slate-100 text-sm">What <b>{partner.name}</b> sees via Partner Ranking API and the Embed. Same ranking service — no second calculation.</div>
          </div>
          <div className="flex gap-2 shrink-0 flex-wrap">
            <GhostButton onClick={() => setEmbedOpen(true)} data-testid="preview-open-embed"><Link2 className="w-4 h-4" /> Preview Embed</GhostButton>
            <GoldButton onClick={copyEmbed} data-testid="preview-copy-embed">{copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? "Copied" : "Copy Embed Code"}</GoldButton>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="preview-products">
          {PREVIEW_PRODUCTS.map((p) => (
            <button key={p.key} onClick={() => { setProduct(p.key); if (p.key !== "SPORTSBOOK") setSubtype("ALL"); }} data-testid={`preview-product-${p.key}`}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-heading font-bold uppercase tracking-wide ${product === p.key ? "bg-[#E5B842] text-black" : "bg-[#161616] border border-[rgba(212,175,55,0.22)] text-slate-300 hover:text-[#E5B842]"}`}
              style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}>{p.label}</button>
          ))}
        </div>
        {sportsbookMode && (
          <div className="mt-2 flex flex-wrap gap-2" data-testid="preview-subs">
            {PREVIEW_SUBS.map((s) => (
              <button key={s.key} onClick={() => setSubtype(s.key)} data-testid={`preview-sub-${s.key}`}
                className={`px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-wider ${subtype === s.key ? "text-[#E5B842] border-[#E5B842] border" : "text-slate-400 bg-[#0a0a0a] border border-[rgba(212,175,55,0.18)]"}`}>{s.label}</button>
            ))}
          </div>
        )}
        {sportsbookMode && data?.rating_status === "RATING_NOT_AVAILABLE" && (
          <div className="mt-3 text-[11px] font-mono text-amber-300/90 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2" data-testid="preview-sportsbook-note">
            RATING_NOT_AVAILABLE — Sportsbook activity view. WSTF Sportsbook rating formula is not yet defined.
          </div>
        )}
      </div>

      <div className="overflow-x-auto rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.20)]" data-testid="preview-table-wrap">
        {data === null ? (
          <Loader label="Loading preview…" />
        ) : rows.length === 0 ? (
          <Empty label="No linked players ranked for this view" />
        ) : (
          <table className="w-full text-sm" data-testid="preview-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-500 font-mono border-b border-[rgba(212,175,55,0.14)]">
                <th className="text-left px-4 py-2.5">Rank</th>
                <th className="text-left px-4 py-2.5">Player</th>
                <th className="text-left px-4 py-2.5">Country</th>
                <th className="text-right px-4 py-2.5">World</th>
                <th className="text-right px-4 py-2.5">{sportsbookMode ? "Settled" : "Rating"}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgba(212,175,55,0.08)]">
              {rows.map((it) => (
                <tr key={it.partner_rank} data-testid={`preview-row-${it.partner_rank}`} className="text-slate-300">
                  <td className="px-4 py-2.5 font-heading font-black text-[#E5B842]">#{it.partner_rank}</td>
                  <td className="px-4 py-2.5">{it.player?.display_name}</td>
                  <td className="px-4 py-2.5 text-slate-400">{it.player?.country?.name || "—"}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-slate-400 tabular-nums">{it.world_rank ? `#${it.world_rank}` : "—"}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-[#E5B842] tabular-nums font-black">
                    {sportsbookMode ? (it.activity?.settled ?? 0) : (it.rating ?? "—")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="rounded-xl bg-[#0a0a0a] border border-[rgba(212,175,55,0.14)] p-4" data-testid="embed-code-block">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Embed code (paste into partner site)</div>
          <button onClick={copyEmbed} data-testid="embed-code-copy" className="text-[11px] font-mono uppercase tracking-wider text-slate-400 hover:text-[#E5B842] inline-flex items-center gap-1" style={{ transition: "color 0.15s ease" }}>{copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Copied" : "Copy"}</button>
        </div>
        <code className="block overflow-x-auto bg-[#000] border border-[rgba(212,175,55,0.14)] rounded px-3 py-2 text-xs text-slate-200 font-mono whitespace-pre">{embedCode}</code>
        <div className="mt-2 text-[10px] font-mono text-slate-600">
          No partner secret is included. Embed uses the public WSTF ranking service — same data as the Partner Ranking API.
        </div>
      </div>

      {embedOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setEmbedOpen(false)} data-testid="embed-preview-modal">
          <div className="w-full max-w-5xl rounded-xl bg-[#0a0a0a] border border-[rgba(212,175,55,0.30)] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[rgba(212,175,55,0.18)]">
              <div className="text-xs font-mono uppercase tracking-wider text-slate-400">Live preview · {embedUrl}</div>
              <button onClick={() => setEmbedOpen(false)} data-testid="embed-preview-close" className="text-slate-400 hover:text-[#E5B842] text-sm" style={{ transition: "color 0.15s ease" }}>Close</button>
            </div>
            <iframe title={`WSTF Ranking Preview — ${partner.name}`} src={embedUrl} className="w-full h-[70vh] bg-black" data-testid="embed-preview-iframe" />
          </div>
        </div>
      )}
    </div>
  );
}


export default function AdminPartners() {
  const { id } = useParams();
  return id ? <PartnerDetail id={id} /> : <PartnerList />;
}
